import Resolver from './Resolver'
import ResolveResult from './ResolveResult'
import QRCodeType from '../models/QRCodeType'
import { getGroupCouponInfo } from '@/views/group-coupon/api'
import { message } from 'ant-design-vue'

/**
 * 团餐优惠券路径解析器
 *
 * @class GroupCouponPathResolver
 * @extends Resolver
 * @description
 * - 处理类型为QRCodeType.GroupCoupon(14)的二维码
 * - 验证优惠券参数完整性(restaurantId, couponId)
 * - 调用API验证优惠券有效性(库存、有效期等)
 * - 跳转到团餐优惠券领取页面
 */
export default class GroupCouponPathResolver extends Resolver {
  /**
   * 执行团餐优惠券路径解析逻辑
   *
   * @returns {Promise<ResolveResult>} 路径解析结果
   * @description
   * - 验证必要参数: type(t)=14, restaurantId(rid), couponId(cid)
   * - 检查type是否为团餐优惠券类型(QRCodeType.GroupCoupon)
   * - 调用API验证优惠券是否有效(存在、未售罄等)
   * - 返回跳转到groupCouponClaim页面的路由信息
   *
   * @throws {Error} 当参数不完整或优惠券无效时返回isResolved=false或错误提示
   */
  async resolvePath(): Promise<ResolveResult> {
    const { t: type, rid: restaurantId, cid: couponId } = this._pathQuery || {}

    // 验证参数完整性
    if (!type || !restaurantId || !couponId) {
      return Promise.resolve({ isResolved: false })
    }

    // 检查type是否为团餐类型
    if (parseInt(type) !== QRCodeType.GroupCoupon) {
      return Promise.resolve({ isResolved: false })
    }

    // 调用API验证优惠券有效性
    try {
      const result = await getGroupCouponInfo(restaurantId, couponId)

      // 验证通过，跳转到领取页面
      // 使用JSON.stringify将数据打包到params中（Memory History模式）
      return Promise.resolve({
        isResolved: true,
        pathName: 'groupCouponClaim',
        params: {
          groupCouponData: JSON.stringify(result.data)
        }
      })
    } catch (error) {
      // API调用失败，显示错误提示
      const e = error as { code: number; message: string }
      message.error(e.message || '优惠券加载失败')

      // 返回解析失败
      return Promise.reject(new Error(e.message || 'invalidCoupon'))
    }
  }
}
